import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ViewerActionName } from "../../../api/threads/liveMessages";
import { reopenThread } from "../../../api/threads/threadHostingApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import ForkIcon from "../../../ui/icons/ForkIcon";
import StartBatchDialog from "../../batches/StartBatchDialog";
import SaveAsWorkflowDialog from "../../workflowSaving/SaveAsWorkflowDialog";
import type { MemberCandidate } from "../hosting/HandOffDialog";
import { notifyThreadListChanged } from "../data/threadListEvents";
import HandOffDialog from "../hosting/HandOffDialog";
import { threadActionLayout, type ThreadAction } from "./threadActionLayout";
import { useThreadContext } from "./threadPageContext";
import { allows } from "./viewerActions";
import type { StepRunner } from "./useStepRunner";

interface ThreadActionsProps {
  lastEntryNumber: number;
  members: readonly MemberCandidate[];
  /** Leaves the Workflow, or resumes it (T-7). */
  runner: StepRunner;
}

type OpenDialog = "handOff" | "batch" | "saveAsWorkflow" | null;

/**
 * What can be done with the Thread, each one button on the header's row (F67): Fork, Save as
 * Workflow, Run on many documents, Hand off, and leaving or resuming the Workflow (T-7, T-9).
 * Where it runs, and on which agent, is the hosting line's (F55); what shows beside the
 * Thread is the split view's, through the top bar (F34, WA-10).
 */
export default function ThreadActions({ lastEntryNumber, members, runner }: ThreadActionsProps) {
  const { t } = useTranslation();
  const { threadId, overview, onForkAt } = useThreadContext();
  // Module C's decisions for this viewer (W-22 slice 4).
  const may = (action: ViewerActionName) => allows(overview.viewer, action);
  const [dialog, setDialog] = useState<OpenDialog>(null);
  const { state, submit } = useSubmission();
  // A batch runs this Thread's vetted Workflow, route and profile on its connector (UC-8).
  const batchSource =
    may("run_on_many") && overview.workflow !== null && overview.attachedConnector !== null && overview.bindings.corpus_version !== null && overview.settings.route !== null
      ? {
          projectId: overview.projectId,
          threadId,
          title: overview.title,
          workflow: overview.workflow,
          contentVersion: overview.bindings.content_version,
          corpusVersion: overview.bindings.corpus_version,
          threadSettings: overview.settings,
          connectorId: overview.attachedConnector,
        }
      : null;
  const layout = threadActionLayout({
    reopen: may("reopen"),
    fork: onForkAt !== null,
    saveAsWorkflow: may("save_as_workflow"),
    runOnManyDocuments: batchSource !== null,
    handOff: may("offer_hosting"),
    leaveWorkflow: may("leave_workflow"),
    resumeWorkflow: may("resume_workflow"),
    mayWrite: may("post_message"),
  });
  const runnerBusy = runner.state.status === "submitting";

  const act = (action: ThreadAction) => {
    switch (action) {
      case "reopen":
        submit(async () => {
          await reopenThread(threadId);
          notifyThreadListChanged();
        });
        return;
      case "fork":
        onForkAt?.(lastEntryNumber);
        return;
      case "saveAsWorkflow":
      case "runOnManyDocuments":
      case "handOff":
        setDialog(action === "runOnManyDocuments" ? "batch" : action);
        return;
      case "leaveWorkflow":
        runner.act("left", null);
        return;
      case "resumeWorkflow":
        runner.act("resumed", null);
    }
  };
  const disabled = (action: ThreadAction) => (action === "reopen" ? state.status === "submitting" : (action === "leaveWorkflow" || action === "resumeWorkflow") && runnerBusy);

  return (
    <>
      {layout.actions.map((action) => (
        <AppButton
          key={action}
          variant={layout.leading === action ? "primary" : "secondary"}
          size="xs"
          disabled={disabled(action)}
          title={action === "fork" ? t("threads.fork.explanation") : undefined}
          onClick={() => { act(action); }}
        >
          {action === "fork" ? <ForkIcon /> : null}
          {t(ACTION_LABEL_KEYS[action])}
        </AppButton>
      ))}
      {state.status === "failed" ? <ErrorNotice error={state.error} compact={true} className="w-full text-xs" /> : null}
      {dialog === "batch" && batchSource !== null ? <StartBatchDialog source={batchSource} onClose={() => { setDialog(null); }} /> : null}
      {dialog === "handOff" ? <HandOffDialog members={members} lastEntryNumber={lastEntryNumber} onClose={() => { setDialog(null); }} /> : null}
      {dialog === "saveAsWorkflow" ? <SaveAsWorkflowDialog threadId={threadId} onClose={() => { setDialog(null); }} /> : null}
    </>
  );
}

const ACTION_LABEL_KEYS: Record<ThreadAction, string> = {
  reopen: "threads.actions.reopen",
  fork: "threads.fork.atLatest",
  saveAsWorkflow: "workflowSaving.open",
  runOnManyDocuments: "batches.start.open",
  handOff: "threads.actions.handOff",
  leaveWorkflow: "threads.workflow.leave",
  resumeWorkflow: "threads.workflow.resume",
};
